import { useMutation, useQueryClient } from '@tanstack/react-query'
import type {
  CreateBuiltInInstrumentInput,
  IBuiltInInstrumentDetail,
} from '@brushfire/types'
import { apiClient } from '@/api/http/client'
import type { ApiResponse } from '@/api/http/types'
import { builtInInstrumentKeys } from '@/features/built-in-instruments/api/built-in-instruments-queries'

export async function createBuiltInInstrument(
  input: CreateBuiltInInstrumentInput,
): Promise<IBuiltInInstrumentDetail> {
  const { data: envelope } = await apiClient.post<ApiResponse<IBuiltInInstrumentDetail>>(
    '/api/v1/built-in-instruments',
    input,
  )
  if (!envelope?.data?.id) {
    throw new Error('Built-in instrument create response missing data')
  }
  return envelope.data
}

export async function updateBuiltInInstrument(
  id: string,
  input: CreateBuiltInInstrumentInput,
): Promise<IBuiltInInstrumentDetail> {
  const { data: envelope } = await apiClient.put<ApiResponse<IBuiltInInstrumentDetail>>(
    `/api/v1/built-in-instruments/${encodeURIComponent(id)}`,
    input,
  )
  if (!envelope?.data?.id) {
    throw new Error('Built-in instrument update response missing data')
  }
  return envelope.data
}

export async function deleteBuiltInInstrument(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/built-in-instruments/${encodeURIComponent(id)}`)
}

export function useCreateBuiltInInstrument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: createBuiltInInstrument,
    onSuccess: () => {
      Promise.resolve(qc.invalidateQueries({ queryKey: builtInInstrumentKeys.all })).catch(
        () => undefined,
      )
    },
  })
}

export function useUpdateBuiltInInstrument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CreateBuiltInInstrumentInput }) =>
      updateBuiltInInstrument(id, input),
    onSuccess: () => {
      Promise.resolve(qc.invalidateQueries({ queryKey: builtInInstrumentKeys.all })).catch(
        () => undefined,
      )
    },
  })
}

export function useDeleteBuiltInInstrument() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteBuiltInInstrument,
    onSuccess: () => {
      Promise.resolve(qc.invalidateQueries({ queryKey: builtInInstrumentKeys.all })).catch(
        () => undefined,
      )
    },
  })
}
